import React, { useState, useRef, useEffect } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { StepperComponent } from "../../../../../_start/assets/ts/components";
import { KTSVG } from "../../../../../_start/helpers";
import { defaultCreateAppData, ICreateAppData, TProjectMotivation } from "./IAppModels";
import { db } from "../../../../../config/firebase";
import {
  doc,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import { useSubscription } from "../../../../../hooks/useSubscription";
import { useProjectTemplate } from "../../../../../hooks/useProjectTemplate";
import { PROJECT_TEMPLATES, ProjectTemplate } from "../../../../../config/projectTemplates";
import { Team, TeamMember, Collaborator, TeamRole } from "../../../../../types/team";

// ─── Role options for team step (mirrors ShareProjectModal) ─────────────────

const ROLE_OPTIONS: {
  role: TeamRole;
  label: string;
  description: string;
}[] = [
  {
    role: "strategist",
    label: "Strategist",
    description: "Can fill market, customer & product steps",
  },
  {
    role: "executor",
    label: "Executor",
    description: "Can fill business model & validation steps",
  },
  {
    role: "viewer",
    label: "Viewer",
    description: "Read-only access to all step data",
  },
];

// ─── Props ────────────────────────────────────────────────────────────────────

interface PrefillData {
  projectName?: string;
  projectDescription?: string;
}

type Props = {
  show: boolean;
  handleClose: () => void;
  userID: string;
  prefillData?: PrefillData;
};

// ─── Component ────────────────────────────────────────────────────────────────

const CreateAppModal: React.FC<Props> = ({ show, handleClose, userID, prefillData }) => {
  const navigate = useNavigate();
  const stepperRef = useRef<HTMLDivElement | null>(null);
  const stepper = useRef<StepperComponent | null>(null);
  const [data, setData] = useState<ICreateAppData>(defaultCreateAppData);
  const [hasError, setHasError] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ProjectTemplate | null>(null);
  const { canUseFeature, isAgency } = useSubscription();
  const { applyTemplate } = useProjectTemplate();
  const canAutoFill = canUseFeature("ai_autofill");

  // ── Team-related state ──────────────────────────────────────────────────────
  const [team, setTeam] = useState<Team | null>(null);
  const [teamLoaded, setTeamLoaded] = useState(false);
  const [modalEntered, setModalEntered] = useState(false);
  const [shareWithTeam, setShareWithTeam] = useState(false);
  const [teamSelections, setTeamSelections] = useState<
    Record<string, { checked: boolean; role: TeamRole }>
  >({});

  // ── Derived ─────────────────────────────────────────────────────────────────
  // Show step 5 only for Agency users who have a team
  const hasTeamStep = isAgency && team !== null;

  // Non-owner members available for sharing
  const shareableMembers: TeamMember[] = team
    ? team.members.filter((m) => m.uid !== userID)
    : [];

  // Build collaborators list from selections
  const teamCollaborators: Collaborator[] = shareableMembers
    .filter((m) => teamSelections[m.uid]?.checked)
    .map((m) => ({
      uid: m.uid,
      email: m.email,
      role: teamSelections[m.uid]?.role ?? "viewer",
      joinedAt: new Date().toISOString(),
    }));

  // ── Load team when modal opens ───────────────────────────────────────────────
  useEffect(() => {
    if (!show) {
      // Reset all state on close
      setData(defaultCreateAppData);
      setHasError(false);
      setIsCreating(false);
      setSelectedTemplate(null);
      setTeam(null);
      setTeamLoaded(false);
      setModalEntered(false);
      setShareWithTeam(false);
      setTeamSelections({});
      return;
    }

    if (!isAgency) {
      setTeamLoaded(true); // Non-agency: no team step, skip loading
      return;
    }

    setTeamLoaded(false);
    loadTeamForModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, isAgency, userID]);

  async function loadTeamForModal() {
    try {
      // Check teams where user is owner
      const ownedQ = query(
        collection(db, "teams"),
        where("createdBy", "==", userID)
      );
      const ownedSnap = await getDocs(ownedQ);
      if (!ownedSnap.empty && ownedSnap.docs[0]) {
        const d = ownedSnap.docs[0];
        setTeam({ id: d.id, ...(d.data() as Omit<Team, "id">) });
        return;
      }
      // Check teams where user is a member
      const memberQ = query(
        collection(db, "teams"),
        where("memberUIDs", "array-contains", userID)
      );
      const memberSnap = await getDocs(memberQ);
      if (!memberSnap.empty && memberSnap.docs[0]) {
        const d = memberSnap.docs[0];
        setTeam({ id: d.id, ...(d.data() as Omit<Team, "id">) });
      }
    } catch (err) {
      console.error("Failed to load team for wizard:", err);
    } finally {
      setTeamLoaded(true);
    }
  }

  // Initialise teamSelections when team is loaded
  useEffect(() => {
    if (!team) return;
    const init: Record<string, { checked: boolean; role: TeamRole }> = {};
    team.members
      .filter((m) => m.uid !== userID)
      .forEach((m) => {
        init[m.uid] = { checked: false, role: "viewer" };
      });
    setTeamSelections(init);
  }, [team, userID]);

  // ── Apply prefill data ───────────────────────────────────────────────────────
  useEffect(() => {
    if (prefillData) {
      setData((prev) => ({
        ...prev,
        projectName: prefillData.projectName || prev.projectName,
        projectDescription: prefillData.projectDescription || prev.projectDescription,
        useAI: prefillData.projectDescription
          ? prefillData.projectDescription.length >= 50
          : prev.useAI,
      }));
    }
  }, [prefillData]);

  // ── Initialise stepper after modal enters AND team data is ready ─────────────
  useEffect(() => {
    if (modalEntered && teamLoaded) {
      stepper.current = StepperComponent.createInsance(
        stepperRef.current as HTMLDivElement
      );
      // Reset to step 1 in case the modal was previously opened
      stepper.current?.goto(1);
    }
  }, [modalEntered, teamLoaded]);

  const handleModalEntered = () => {
    setModalEntered(true);
  };

  // ── Validation helpers ───────────────────────────────────────────────────────
  const checkProjectName = (): boolean => !!data.projectName;
  const checkProjectMotivation = (): boolean => !!data.projectMotivation;

  // ── Navigation ───────────────────────────────────────────────────────────────
  const prevStep = () => {
    if (!stepper.current) return;
    stepper.current.goPrev();
  };

  const nextStep = () => {
    setHasError(false);
    if (!stepper.current) return;

    const step = stepper.current.getCurrentStepIndex();

    if (step === 1 && !checkProjectName()) {
      setHasError(true);
      return;
    }
    if (step === 3 && !checkProjectMotivation()) {
      setHasError(true);
      return;
    }

    stepper.current.goNext();
  };

  const updateData = (fieldsToUpdate: Partial<ICreateAppData>) => {
    setData((prev) => ({ ...prev, ...fieldsToUpdate }));
  };

  // ── Team selection helpers ───────────────────────────────────────────────────
  const handleToggleMember = (uid: string) => {
    setTeamSelections((prev) => ({
      ...prev,
      [uid]: { ...prev[uid]!, checked: !prev[uid]!.checked },
    }));
  };

  const handleRoleChange = (uid: string, role: TeamRole) => {
    setTeamSelections((prev) => ({
      ...prev,
      [uid]: { ...prev[uid]!, role },
    }));
  };

  // ── Create project in Firestore ──────────────────────────────────────────────
  const createProject = async (uid: string): Promise<string> => {
    const userRef = doc(db, "users", uid);
    const projectsRef = collection(userRef, "projects");
    const projectData = {
      projectTitle: data.projectName,
      projectProgress: 0,
      projectDescription: data.projectDescription,
      projectMotivation: data.projectMotivation,
      aiGenerated: data.useAI && data.projectDescription.trim().length >= 50,
      createdAt: new Date().toISOString(),
    };
    const projectDoc = await addDoc(projectsRef, projectData);
    return projectDoc.id;
  };

  // ── Submit ───────────────────────────────────────────────────────────────────
  const submit = async (uid: string) => {
    setIsCreating(true);
    try {
      const projectID = await createProject(uid);

      // Apply template if selected
      if (selectedTemplate) {
        await applyTemplate(uid, projectID, selectedTemplate);
      }

      // Apply team sharing if enabled and collaborators selected
      if (shareWithTeam && team && teamCollaborators.length > 0) {
        const projectRef = doc(db, "users", uid, "projects", projectID);
        await updateDoc(projectRef, {
          collaborators: teamCollaborators,
          collaboratorUIDs: teamCollaborators.map((c) => c.uid),
          // viewerUIDs denormalized for Firestore security rules: viewer-role
          // collaborators are blocked from project/progress writes at rules layer.
          viewerUIDs: teamCollaborators.filter((c) => c.role === "viewer").map((c) => c.uid),
          teamId: team.id,
        });
        await updateDoc(doc(db, "teams", team.id), {
          projectIds: arrayUnion(projectID),
        });
      }

      handleClose();

      if (!selectedTemplate && data.useAI && data.projectDescription.trim().length >= 50) {
        navigate("/ai-progress", {
          state: {
            userID: uid,
            projectID,
            projectName: data.projectName,
            projectDescription: data.projectDescription,
          },
        });
      } else {
        navigate("/project", {
          state: {
            userID: uid,
            projectID,
            projectTitle: selectedTemplate ? selectedTemplate.name : data.projectName,
            projectDescription: selectedTemplate
              ? selectedTemplate.description
              : data.projectDescription,
          },
        });
      }
    } catch (error) {
      console.error("Failed to create project:", error);
      setHasError(true);
      setIsCreating(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Modal
      id="kt_modal_create_app"
      tabIndex={-1}
      aria-hidden="true"
      dialogClassName="modal-dialog-centered mw-1000px h-auto"
      show={show}
      onHide={handleClose}
      onEntered={handleModalEntered}
    >
      <div className="container px-10 py-10">
        <div className="modal-header py-2 d-flex justify-content-end border-0">
          <div
            className="btn btn-icon btn-sm btn-light-primary"
            onClick={handleClose}
          >
            <KTSVG
              className="svg-icon-2"
              path="/media/icons/duotone/Navigation/Close.svg"
            />
          </div>
        </div>

        <div className="modal-body">
          {/* Loading state while team data fetches */}
          {show && !teamLoaded && (
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ minHeight: 320 }}
            >
              <span className="spinner-border text-primary" role="status" aria-label="Loading" />
            </div>
          )}

          {/* Stepper — rendered once team data is ready */}
          {teamLoaded && (
            <div
              ref={stepperRef}
              className="stepper stepper-1 d-flex flex-column flex-xl-row flex-row-fluid"
              id="kt_modal_create_app_stepper"
            >
              {/*begin::Aside — Nav */}
              <div className="d-flex justify-content-center justify-content-xl-start flex-row-auto w-100 w-xl-300px">
                <div className="stepper-nav d-flex flex-column pt-5">

                  {/* Step 1 */}
                  <div className="stepper-item current" data-kt-stepper-element="nav">
                    <div className="stepper-wrapper">
                      <div className="stepper-icon">
                        <i className="stepper-check fas fa-check"></i>
                        <span className="stepper-number">1</span>
                      </div>
                      <div className="stepper-label">
                        <h3 className="stepper-title">New Project</h3>
                        <div className="stepper-desc">Name your Business Idea</div>
                      </div>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="stepper-item" data-kt-stepper-element="nav">
                    <div className="stepper-wrapper">
                      <div className="stepper-icon">
                        <i className="stepper-check fas fa-check"></i>
                        <span className="stepper-number">2</span>
                      </div>
                      <div className="stepper-label">
                        <h3 className="stepper-title">Project Description</h3>
                        <div className="stepper-desc">Describe your idea</div>
                      </div>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="stepper-item" data-kt-stepper-element="nav">
                    <div className="stepper-wrapper">
                      <div className="stepper-icon">
                        <i className="stepper-check fas fa-check"></i>
                        <span className="stepper-number">3</span>
                      </div>
                      <div className="stepper-label">
                        <h3 className="stepper-title">Motivation</h3>
                        <div className="stepper-desc">What is your motivation</div>
                      </div>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="stepper-item" data-kt-stepper-element="nav">
                    <div className="stepper-wrapper">
                      <div className="stepper-icon">
                        <i className="stepper-check fas fa-check"></i>
                        <span className="stepper-number">4</span>
                      </div>
                      <div className="stepper-label">
                        <h3 className="stepper-title">
                          {hasTeamStep ? "Review" : "Complete!"}
                        </h3>
                        <div className="stepper-desc">
                          {hasTeamStep ? "Review your project" : "Review and Create"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Step 5 — Team Setup (Agency + team only) */}
                  {hasTeamStep && (
                    <div className="stepper-item" data-kt-stepper-element="nav">
                      <div className="stepper-wrapper">
                        <div className="stepper-icon">
                          <i className="stepper-check fas fa-check"></i>
                          <span className="stepper-number">5</span>
                        </div>
                        <div className="stepper-label">
                          <h3 className="stepper-title">Team Setup</h3>
                          <div className="stepper-desc">Share with your team</div>
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              </div>
              {/*end::Aside */}

              {/*begin::Content */}
              <div className="d-flex flex-row-fluid justify-content-center">
                <form
                  className="pb-5 w-100 w-md-400px w-xl-500px"
                  noValidate
                  id="kt_modal_create_app_form"
                >
                  {/*begin::Step 1 */}
                  <div className="pb-5 current" data-kt-stepper-element="content">
                    <div className="w-100">
                      <div className="pb-5 pb-lg-10">
                        <h3 className="fw-bolder text-dark display-6">New Project</h3>
                      </div>

                      {/* Project name */}
                      <div className="fv-row mb-12">
                        <label className="fs-6 fw-bolder text-dark form-label">
                          Give Your Business Idea a Name
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-lg form-control-solid"
                          name="appname"
                          placeholder=""
                          value={data.projectName}
                          onChange={(e) => updateData({ projectName: e.target.value })}
                        />
                        <span>Don't worry, you can change it anytime later.</span>
                        {!data.projectName && hasError && (
                          <div className="fv-plugins-message-container">
                            <div
                              data-field="appname"
                              data-validator="notEmpty"
                              className="fv-help-block"
                            >
                              Project name is required
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Template selector */}
                      <div className="fv-row mt-8">
                        <label className="fs-6 fw-bolder text-dark mb-3 d-block">
                          Start From a Template
                          <span className="fs-8 fw-normal text-muted ms-2">
                            (optional — customise afterwards)
                          </span>
                        </label>
                        <div
                          className="d-grid gap-3"
                          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}
                        >
                          {/* Scratch option */}
                          <label
                            className="d-flex flex-column align-items-center p-4 rounded cursor-pointer"
                            style={{
                              border: `2px solid ${selectedTemplate === null ? "#3B82F6" : "#E2E8F0"}`,
                              background: selectedTemplate === null ? "#DBEAFE" : "#FFFFFF",
                              transition: "border-color 0.15s, background 0.15s",
                            }}
                            onClick={() => setSelectedTemplate(null)}
                          >
                            <i
                              className="bi bi-pencil-square fs-2x mb-2"
                              style={{ color: "#475569" }}
                            ></i>
                            <span className="fw-bold fs-7 text-dark text-center">
                              Start from scratch
                            </span>
                          </label>

                          {PROJECT_TEMPLATES.map((tpl) => (
                            <label
                              key={tpl.id}
                              className="d-flex flex-column align-items-center p-4 rounded cursor-pointer"
                              style={{
                                border: `2px solid ${
                                  selectedTemplate?.id === tpl.id ? "#3B82F6" : "#E2E8F0"
                                }`,
                                background:
                                  selectedTemplate?.id === tpl.id ? "#DBEAFE" : "#FFFFFF",
                                transition: "border-color 0.15s, background 0.15s",
                              }}
                              onClick={() => setSelectedTemplate(tpl)}
                            >
                              <i
                                className={`bi ${tpl.icon} fs-2x mb-2`}
                                style={{ color: "#475569" }}
                              ></i>
                              <span className="fw-bold fs-7 text-dark text-center">
                                {tpl.name}
                              </span>
                              <span className="fs-8 text-muted text-center mt-1">
                                {tpl.description}
                              </span>
                            </label>
                          ))}
                        </div>
                        {selectedTemplate && (
                          <div
                            className="d-flex align-items-center mt-3 p-3 rounded"
                            style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "8px" }}
                          >
                            <i
                              className="bi bi-info-circle me-2"
                              style={{ color: "#475569", fontSize: 16 }}
                            ></i>
                            <span className="fs-7" style={{ color: "#475569" }}>
                              Template pre-fills steps 1–10 with example data.{" "}
                              <strong>You should customise every field</strong> to reflect your
                              actual business.
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {/*end::Step 1 */}

                  {/*begin::Step 2 */}
                  <div className="pb-5" data-kt-stepper-element="content">
                    <div className="w-100">
                      <div className="pb-5 pb-lg-10">
                        <h3 className="fw-bolder text-dark display-6">
                          Describe Your Business Idea
                        </h3>
                        <p className="text-muted fs-6 mt-3">
                          Tell us about your business in your own words. Our AI will analyze your
                          idea and automatically fill in the 24-step validation framework.
                        </p>
                      </div>

                      <div className="fv-row">
                        <label className="fs-6 fw-bolder text-dark mb-3">
                          Business Description
                        </label>
                        <p className="text-muted fs-7 mb-4">
                          Include details about: what problem you solve, who your customers are,
                          how you make money, and what makes you different.
                        </p>
                        <textarea
                          className="form-control form-control-lg form-control-solid"
                          name="projectDescription"
                          rows={8}
                          placeholder="Example: I want to build a subscription box service for pet owners that delivers healthy, organic treats and eco-friendly toys monthly. We target urban millennials (25-40) who treat their pets like family and are willing to pay premium prices for quality products. Our subscription model charges $39/month with 60% gross margins. We differentiate through personalized selections based on pet preferences and sustainable packaging."
                          value={data.projectDescription}
                          onChange={(e) => updateData({ projectDescription: e.target.value })}
                        />
                        <div className="d-flex justify-content-between mt-2">
                          <span className="text-muted fs-7">
                            {data.projectDescription.length} characters
                            {data.projectDescription.length < 100 &&
                              " (minimum 100 recommended)"}
                          </span>
                          {data.projectDescription.length >= 100 && (
                            <span className="text-success fs-7">
                              <i className="bi bi-check-circle-fill text-success me-1"></i>
                              Ready for AI analysis
                            </span>
                          )}
                        </div>
                      </div>

                      {/* AI Toggle */}
                      <div className="fv-row mt-8">
                        <label
                          className={`d-flex align-items-center justify-content-between ${
                            canAutoFill ? "cursor-pointer" : "opacity-50"
                          }`}
                        >
                          <span className="d-flex flex-column">
                            <span className="fw-bolder fs-6 text-dark">
                              <i className="bi bi-magic text-primary me-2"></i>
                              AI-Powered Analysis
                              {!canAutoFill && (
                                <span className="badge badge-light-primary ms-2 fs-9">Pro</span>
                              )}
                            </span>
                            <span className="fs-7 text-muted">
                              {canAutoFill
                                ? "Automatically populate the 24-step validation framework"
                                : "Upgrade to Pro to auto-fill all 24 steps with AI"}
                            </span>
                          </span>
                          <span className="form-check form-switch form-check-custom form-check-solid">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={canAutoFill && data.useAI}
                              onChange={(e) =>
                                canAutoFill && updateData({ useAI: e.target.checked })
                              }
                              disabled={!canAutoFill}
                            />
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                  {/*end::Step 2 */}

                  {/*begin::Step 3 */}
                  <div className="pb-5" data-kt-stepper-element="content">
                    <div className="w-100">
                      <div className="pb-10 pb-lg-15">
                        <h3 className="fw-bolder text-dark display-6">Motivation</h3>
                        <p>
                          To get started with this process, you just need an idea, a technology or
                          an inspiration. But let's be clear which one of these you are starting
                          with and put a little more definition on "it".
                        </p>
                      </div>

                      <div className="fv-row">
                        {/* Option: Idea */}
                        <label
                          className="d-flex align-items-center justify-content-between cursor-pointer mb-3 p-4 rounded"
                          style={{
                            border: `2px solid ${data.projectMotivation === "Idea" ? "#3B82F6" : "#E2E8F0"}`,
                            backgroundColor: data.projectMotivation === "Idea" ? "#DBEAFE" : "#FFFFFF",
                            transition: "border-color 0.15s, background-color 0.15s",
                          }}
                        >
                          <span className="d-flex align-items-center me-2">
                            <i className="fas fa-database me-4" style={{ fontSize: "32px", color: "#475569", flexShrink: 0 }}></i>
                            <span className="d-flex flex-column">
                              <span className="fw-bolder fs-6">I have an idea</span>
                              <span className="fs-7 text-muted">
                                What is the idea? What generated the idea? What makes it unique and
                                better than the current alternatives? Who do you think it would be
                                relevant to? Why are you the one who should bring this idea to market?
                              </span>
                            </span>
                          </span>
                          <span className="form-check form-check-custom form-check-solid ms-4">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="selectMotivation"
                              value="Idea"
                              onChange={(e) =>
                                updateData({ projectMotivation: e.target.value as TProjectMotivation })
                              }
                              checked={data.projectMotivation === "Idea"}
                            />
                          </span>
                        </label>

                        {/* Option: Technology */}
                        <label
                          className="d-flex align-items-center justify-content-between cursor-pointer mb-3 p-4 rounded"
                          style={{
                            border: `2px solid ${data.projectMotivation === "Technology" ? "#3B82F6" : "#E2E8F0"}`,
                            backgroundColor: data.projectMotivation === "Technology" ? "#DBEAFE" : "#FFFFFF",
                            transition: "border-color 0.15s, background-color 0.15s",
                          }}
                        >
                          <span className="d-flex align-items-center me-2">
                            <i className="fab fa-google me-4" style={{ fontSize: "32px", color: "#475569", flexShrink: 0 }}></i>
                            <span className="d-flex flex-column">
                              <span className="fw-bolder fs-6">I have a technology</span>
                              <span className="fs-7 text-muted">
                                What is the technology? Where did it come from? What makes it unique
                                and better than the current alternatives? Who do you think it would be
                                relevant to? Why are you the one to bring it to market?
                              </span>
                            </span>
                          </span>
                          <span className="form-check form-check-custom form-check-solid ms-4">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="selectMotivation"
                              value="Technology"
                              checked={data.projectMotivation === "Technology"}
                              onChange={(e) =>
                                updateData({ projectMotivation: e.target.value as TProjectMotivation })
                              }
                            />
                          </span>
                        </label>

                        {/* Option: Inspiration */}
                        <label
                          className="d-flex align-items-center justify-content-between cursor-pointer mb-3 p-4 rounded"
                          style={{
                            border: `2px solid ${data.projectMotivation === "Inspiration" ? "#3B82F6" : "#E2E8F0"}`,
                            backgroundColor: data.projectMotivation === "Inspiration" ? "#DBEAFE" : "#FFFFFF",
                            transition: "border-color 0.15s, background-color 0.15s",
                          }}
                        >
                          <span className="d-flex align-items-center me-2">
                            <i className="fab fa-amazon me-4" style={{ fontSize: "32px", color: "#475569", flexShrink: 0 }}></i>
                            <span className="d-flex flex-column">
                              <span className="fw-bolder fs-6">I have inspiration</span>
                              <span className="fs-7 text-muted">
                                Inspiration is crucial as every entrepreneur needs it. You want to be
                                an entrepreneur but you have to get either an idea or a technology or
                                a co-founder who can help you zero in on one of these two things.
                              </span>
                            </span>
                          </span>
                          <span className="form-check form-check-custom form-check-solid ms-4">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="selectMotivation"
                              value="Inspiration"
                              checked={data.projectMotivation === "Inspiration"}
                              onChange={(e) =>
                                updateData({ projectMotivation: e.target.value as TProjectMotivation })
                              }
                            />
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                  {/*end::Step 3 */}

                  {/*begin::Step 4 — Review */}
                  <div className="pb-5" data-kt-stepper-element="content">
                    <div className="w-100">
                      <div className="pb-10 pb-lg-15">
                        <h3 className="fw-bolder text-dark display-6">
                          {hasTeamStep ? "Review Your Project" : "Ready to Create"}
                        </h3>
                        <div className="text-muted fw-bold fs-5">
                          {hasTeamStep
                            ? "Check your details then configure team access on the next step"
                            : "Review your project and let AI analyze your business idea"}
                        </div>
                      </div>

                      {/* Project name */}
                      <div className="d-flex align-items-center mb-6">
                        <div className="symbol symbol-40px me-4">
                          <div className="symbol-label bg-light-primary">
                            <i className="bi bi-briefcase text-primary fs-4"></i>
                          </div>
                        </div>
                        <div className="flex-grow-1">
                          <h4 className="fw-bolder mb-0">{data.projectName}</h4>
                          <span className="text-muted fs-7">Project Name</span>
                        </div>
                      </div>

                      {/* Description */}
                      <div className="d-flex align-items-start mb-6">
                        <div className="symbol symbol-40px me-4">
                          <div className="symbol-label bg-light-info">
                            <i className="bi bi-file-text text-info fs-4"></i>
                          </div>
                        </div>
                        <div className="flex-grow-1">
                          <h4 className="fw-bolder mb-1">Description</h4>
                          <p
                            className="text-gray-600 fs-7 mb-0"
                            style={{ maxHeight: "100px", overflow: "auto" }}
                          >
                            {data.projectDescription || "No description provided"}
                          </p>
                        </div>
                      </div>

                      {/* Motivation */}
                      <div className="d-flex align-items-center mb-6">
                        <div className="symbol symbol-40px me-4">
                          <div className="symbol-label bg-light-warning">
                            <i className="bi bi-lightbulb text-warning fs-4"></i>
                          </div>
                        </div>
                        <div className="flex-grow-1">
                          <h4 className="fw-bolder mb-0">{data.projectMotivation}</h4>
                          <span className="text-muted fs-7">Motivation</span>
                        </div>
                      </div>

                      {/* AI info */}
                      {data.useAI && data.projectDescription.length >= 50 && (
                        <div className="notice d-flex bg-light-primary rounded border-primary border border-dashed p-6 mt-6">
                          <i className="bi bi-magic fs-2tx text-primary me-4"></i>
                          <div className="d-flex flex-stack flex-grow-1">
                            <div className="fw-bold">
                              <h4 className="text-gray-800 fw-bolder">AI Analysis Enabled</h4>
                              <div className="fs-6 text-gray-600">
                                When you submit, AI will analyze your business idea and automatically
                                populate the 24-step validation framework. You can review and edit
                                each step afterwards.
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Creating progress */}
                      {isCreating && (
                        <div className="mt-8">
                          <div className="d-flex align-items-center mb-3">
                            <Spinner animation="border" size="sm" className="me-3 text-primary" />
                            <span className="fw-bold text-gray-700">
                              Creating your project...
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {/*end::Step 4 */}

                  {/*begin::Step 5 — Team Setup (Agency + team only) */}
                  {hasTeamStep && (
                    <div className="pb-5" data-kt-stepper-element="content">
                      <div className="w-100">
                        <div className="pb-6 pb-lg-10">
                          <h3 className="fw-bolder text-dark display-6">Team Setup</h3>
                          <p className="text-muted fs-6 mt-2">
                            Optionally share this project with your team members and assign their
                            roles before you start.
                          </p>
                        </div>

                        {/* Share toggle */}
                        <div
                          className="d-flex align-items-center justify-content-between p-4 rounded mb-5 cursor-pointer"
                          style={{
                            border: `1px solid ${shareWithTeam ? "#3B82F6" : "#E2E8F0"}`,
                            background: shareWithTeam ? "#DBEAFE" : "#F8FAFC",
                            transition: "all 150ms ease-out",
                          }}
                          onClick={() => setShareWithTeam((v) => !v)}
                        >
                          <div className="d-flex align-items-center" style={{ gap: 12 }}>
                            <div
                              style={{
                                width: 36,
                                height: 36,
                                borderRadius: "50%",
                                background: shareWithTeam ? "#DBEAFE" : "#F1F5F9",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <i
                                className="bi bi-people-fill"
                                style={{
                                  fontSize: 14,
                                  color: shareWithTeam ? "#1D4ED8" : "#94A3B8",
                                }}
                              />
                            </div>
                            <div>
                              <div className="fw-bolder fs-6 text-dark">
                                Share with team — {team!.name}
                              </div>
                              <div className="fs-7 text-muted">
                                Team members will have access to this project
                              </div>
                            </div>
                          </div>
                          <span className="form-check form-switch form-check-custom form-check-solid ms-4">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={shareWithTeam}
                              onChange={(e) => setShareWithTeam(e.target.checked)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </span>
                        </div>

                        {/* Member list — only when sharing enabled */}
                        {shareWithTeam && (
                          <div>
                            {shareableMembers.length === 0 ? (
                              <div
                                style={{
                                  textAlign: "center",
                                  padding: "28px 16px",
                                  color: "#94A3B8",
                                  fontSize: 14,
                                  background: "#F8FAFC",
                                  borderRadius: 10,
                                  border: "1px dashed #CBD5E1",
                                }}
                              >
                                <i
                                  className="bi bi-people"
                                  style={{ fontSize: 24, display: "block", marginBottom: 8 }}
                                />
                                No team members to share with yet.
                                <br />
                                Invite members from the Team page first.
                              </div>
                            ) : (
                              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                {shareableMembers.map((member) => {
                                  const sel = teamSelections[member.uid];
                                  const isChecked = sel?.checked ?? false;
                                  const initials = member.email.slice(0, 2).toUpperCase();

                                  return (
                                    <div
                                      key={member.uid}
                                      style={{
                                        border: `1px solid ${isChecked ? "#3B82F6" : "#E2E8F0"}`,
                                        borderRadius: 10,
                                        padding: "12px 14px",
                                        background: isChecked ? "#EFF6FF" : "#FFFFFF",
                                        transition: "all 150ms ease-out",
                                      }}
                                    >
                                      {/* Member row */}
                                      <div
                                        className="d-flex align-items-center"
                                        style={{ gap: 10 }}
                                      >
                                        <input
                                          type="checkbox"
                                          id={`wizard-member-${member.uid}`}
                                          checked={isChecked}
                                          onChange={() => handleToggleMember(member.uid)}
                                          style={{
                                            width: 16,
                                            height: 16,
                                            flexShrink: 0,
                                            cursor: "pointer",
                                          }}
                                        />
                                        <div
                                          style={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: "50%",
                                            background: "#DBEAFE",
                                            color: "#1D4ED8",
                                            fontSize: 11,
                                            fontWeight: 700,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0,
                                          }}
                                        >
                                          {initials}
                                        </div>
                                        <label
                                          htmlFor={`wizard-member-${member.uid}`}
                                          style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                                        >
                                          <div
                                            style={{
                                              fontSize: 13,
                                              fontWeight: 600,
                                              color: "#0F172A",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {member.email}
                                          </div>
                                          <div
                                            style={{ fontSize: 11, color: "#94A3B8", marginTop: 1 }}
                                          >
                                            Team {member.role}
                                          </div>
                                        </label>
                                      </div>

                                      {/* Role selector — only shown when checked */}
                                      {isChecked && (
                                        <div
                                          style={{
                                            marginTop: 10,
                                            paddingTop: 10,
                                            borderTop: "1px solid #E2E8F0",
                                            display: "flex",
                                            gap: 6,
                                            flexWrap: "wrap",
                                            alignItems: "center",
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: 12,
                                              fontWeight: 500,
                                              color: "#475569",
                                              marginRight: 2,
                                            }}
                                          >
                                            Access:
                                          </span>
                                          {ROLE_OPTIONS.map(
                                            ({ role, label, description }) => {
                                              const isActive = sel?.role === role;
                                              return (
                                                <button
                                                  key={role}
                                                  type="button"
                                                  onClick={() =>
                                                    handleRoleChange(member.uid, role)
                                                  }
                                                  title={description}
                                                  style={{
                                                    background: isActive ? "#DBEAFE" : "#F1F5F9",
                                                    color: isActive ? "#1D4ED8" : "#475569",
                                                    border: `1px solid ${isActive ? "#BFDBFE" : "#E2E8F0"}`,
                                                    borderRadius: 6,
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    padding: "8px 12px",
                                                    minHeight: 36,
                                                    cursor: "pointer",
                                                    transition: "all 100ms ease-out",
                                                  }}
                                                >
                                                  {label}
                                                </button>
                                              );
                                            }
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Summary when members selected */}
                            {teamCollaborators.length > 0 && (
                              <div
                                className="d-flex align-items-center mt-4 p-3 rounded"
                                style={{
                                  background: "#F8FAFC",
                                  border: "1px solid #E2E8F0",
                                  borderRadius: "8px",
                                }}
                              >
                                <i
                                  className="bi bi-check-circle-fill me-2"
                                  style={{ color: "#475569", fontSize: 14, flexShrink: 0 }}
                                />
                                <span className="fs-7" style={{ color: "#475569" }}>
                                  <strong>{teamCollaborators.length}</strong>{" "}
                                  {teamCollaborators.length === 1 ? "member" : "members"} will be
                                  added when you create this project.
                                </span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Skip note */}
                        {!shareWithTeam && (
                          <p className="text-muted fs-7 mt-4">
                            <i className="bi bi-info-circle me-1" />
                            You can always share this project with your team later from the project
                            dashboard.
                          </p>
                        )}

                        {/* Creating progress */}
                        {isCreating && (
                          <div className="mt-8">
                            <div className="d-flex align-items-center mb-3">
                              <Spinner
                                animation="border"
                                size="sm"
                                className="me-3 text-primary"
                              />
                              <span className="fw-bold text-gray-700">
                                Creating your project...
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {/*end::Step 5 */}

                  {/*begin::Actions */}
                  <div className="d-flex justify-content-between pt-10">
                    <div className="mr-2">
                      <button
                        type="button"
                        className="btn btn-lg btn-light-primary fw-bolder py-4 pe-8 me-3"
                        data-kt-stepper-action="previous"
                        onClick={prevStep}
                        disabled={isCreating}
                      >
                        <KTSVG
                          path="/media/icons/duotone/Navigation/Left-2.svg"
                          className="svg-icon-3 me-1"
                        />{" "}
                        Previous
                      </button>
                    </div>
                    <div>
                      <button
                        type="button"
                        className="btn btn-lg btn-primary fw-bolder py-4 ps-8 me-3"
                        data-kt-stepper-action="submit"
                        onClick={() => submit(userID)}
                        disabled={isCreating}
                      >
                        {isCreating ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Creating...
                          </>
                        ) : (
                          <>
                            {data.useAI && data.projectDescription.length >= 50 ? (
                              <>
                                <i className="bi bi-magic me-2"></i>
                                Create with AI
                              </>
                            ) : (
                              <>
                                Create Project
                                <KTSVG
                                  path="/media/icons/duotone/Navigation/Right-2.svg"
                                  className="svg-icon-3 ms-2"
                                />
                              </>
                            )}
                          </>
                        )}
                      </button>

                      <button
                        type="button"
                        className="btn btn-lg btn-primary fw-bolder py-4 ps-8 me-3"
                        data-kt-stepper-action="next"
                        onClick={nextStep}
                        disabled={isCreating}
                      >
                        Next Step{" "}
                        <KTSVG
                          path="/media/icons/duotone/Navigation/Right-2.svg"
                          className="svg-icon-3 ms-1"
                        />
                      </button>
                    </div>
                  </div>
                  {/*end::Actions */}
                </form>
              </div>
              {/*end::Content */}
            </div>
          )}
          {/* end::Stepper */}
        </div>
      </div>
    </Modal>
  );
};

export { CreateAppModal };
